<template>
  <div
    class="ir-ai-detect-picker"
    :class="{
      'ir-ai-detect-picker--disabled': disabled && !guideOnly,
      'ir-ai-detect-picker--guide': guideOnly,
      'ir-ai-detect-picker--fixed-frame': hasFixedFrame
    }"
  >
    <svg
      v-if="processedUrl"
      ref="svg"
      class="ir-ai-detect-picker__svg rounded"
      :style="svgFrameStyle"
      :viewBox="'0 0 ' + imgW + ' ' + imgH"
      :width="hasFixedFrame ? frameWidthPx : '100%'"
      :height="hasFixedFrame ? frameHeightPx : undefined"
      preserveAspectRatio="xMidYMid meet"
      data-testid="ir-ai-detect-region-picker-svg"
    >
      <image :href="processedUrl" :width="imgW" :height="imgH" preserveAspectRatio="none" />
      <rect
        v-for="(o, idx) in dimOverlays"
        :key="'dim-' + idx"
        class="ir-ai-detect-picker__dim"
        v-bind="o"
      />
      <rect
        :x="rectX"
        :y="rectY"
        :width="rectWidth"
        :height="rectHeight"
        fill="none"
        class="ir-ai-detect-picker__bbox"
      />
      <g v-if="!disabled && !guideOnly">
        <rect
          :x="handles.left.x"
          :y="handles.left.y"
          :width="handles.left.w"
          :height="handles.left.h"
          class="ir-ai-detect-picker__hit"
          @mousedown.stop.prevent="onEdgeMouseDown('left', $event)"
        />
        <rect
          :x="handles.right.x"
          :y="handles.right.y"
          :width="handles.right.w"
          :height="handles.right.h"
          class="ir-ai-detect-picker__hit"
          @mousedown.stop.prevent="onEdgeMouseDown('right', $event)"
        />
        <rect
          :x="handles.top.x"
          :y="handles.top.y"
          :width="handles.top.w"
          :height="handles.top.h"
          class="ir-ai-detect-picker__hit"
          @mousedown.stop.prevent="onEdgeMouseDown('top', $event)"
        />
        <rect
          :x="handles.bottom.x"
          :y="handles.bottom.y"
          :width="handles.bottom.w"
          :height="handles.bottom.h"
          class="ir-ai-detect-picker__hit"
          @mousedown.stop.prevent="onEdgeMouseDown('bottom', $event)"
        />
      </g>
    </svg>
    <div
      v-else
      class="ir-ai-detect-picker__empty d-flex align-center justify-center text--secondary caption"
      :style="emptyFrameStyle"
    />

    <div
      v-if="showWidthLabel"
      class="mt-2 text-body-2 font-weight-medium"
      data-testid="ir-ai-detect-region-width-label"
    >
      {{ widthPercentLabel }}
    </div>
  </div>
</template>

<script>
  import { processImageUrlForCdn } from '@/utils/imagePolygonManager/imageLoader';

  const IMG_W = 640;
  const IMG_H = 512;
  /** 선형 비율 r = 이미지 가로(·세로) 대비 유효 박스 한 변 길이 비율 (기본 0.3 = 30%) */
  const MIN_LINEAR_R = 0.04;
  const FULL_LINEAR_THRESHOLD = 0.997;
  const HANDLE = 36;
  /** 드래그 중 store·퍼센트 표시 갱신 간격(ms) — 열화상 온도 읽기와 비슷한 주기 */
  const COMMIT_INTERVAL_MS = 150;

  export default {
    name: 'IrAiDetectRegionPicker',
    props: {
      value: {
        type: Number,
        default: undefined
      },
      /** 원본 이미지 URL (열화상) */
      imageUrl: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      /** 가로 비율(%) 라벨 i18n 키 (기본 map.aiDefectDisplayWidthPercent) */
      widthPercentI18nKey: {
        type: String,
        default: 'map.aiDefectDisplayWidthPercent'
      },
      /** 지정 시 SVG·빈 영역을 가로 displayMaxPx·세로(비율 유지) 고정 px 로 표시 */
      displayMaxPx: {
        type: Number,
        default: null
      },
      /** 좌·우 가이드: 중앙과 동일 bbox만 표시, 드래그·라벨 없음 */
      guideOnly: {
        type: Boolean,
        default: false
      },
      showWidthLabel: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        imgW: IMG_W,
        imgH: IMG_H,
        cx: IMG_W / 2,
        cy: IMG_H / 2,
        linearInner: null,
        dragEdge: null,
        /** 드래그 중에도 일정 간격으로만 갱신 */
        displayWidthPercent: 100,
        commitTimerId: null,
        pendingLinear: null
      };
    },
    computed: {
      hasFixedFrame() {
        return this.displayMaxPx != null && this.displayMaxPx > 0;
      },
      frameWidthPx() {
        return this.hasFixedFrame ? this.displayMaxPx : null;
      },
      frameHeightPx() {
        if (!this.hasFixedFrame) return null;
        return Math.round((this.displayMaxPx * IMG_H) / IMG_W);
      },
      svgFrameStyle() {
        if (!this.hasFixedFrame) return null;
        return {
          width: `${this.frameWidthPx}px`,
          height: `${this.frameHeightPx}px`,
          maxWidth: `${this.frameWidthPx}px`,
          maxHeight: `${this.frameHeightPx}px`
        };
      },
      emptyFrameStyle() {
        if (!this.hasFixedFrame) return null;
        return {
          width: `${this.frameWidthPx}px`,
          height: `${this.frameHeightPx}px`,
          maxWidth: `${this.frameWidthPx}px`,
          maxHeight: `${this.frameHeightPx}px`,
          minHeight: 0
        };
      },
      processedUrl() {
        return this.imageUrl ? processImageUrlForCdn(this.imageUrl) : '';
      },
      /** null → 전체(시각적으로 r=1) */
      displayLinear() {
        if (this.dragEdge !== null && this.linearInner != null) {
          return this.linearInner;
        }
        return this.value == null ? 1 : this.value;
      },
      rectWidth() {
        return this.displayLinear * this.imgW;
      },
      rectHeight() {
        return this.displayLinear * this.imgH;
      },
      rectX() {
        return this.cx - this.rectWidth / 2;
      },
      rectY() {
        return this.cy - this.rectHeight / 2;
      },
      dimOverlays() {
        const { rectX: x0, rectY: y0, rectWidth: w0, rectHeight: h0 } = this;
        return [
          { x: 0, y: 0, width: this.imgW, height: Math.max(0, y0) },
          {
            x: 0,
            y: y0 + h0,
            width: this.imgW,
            height: Math.max(0, this.imgH - (y0 + h0))
          },
          { x: 0, y: y0, width: Math.max(0, x0), height: h0 },
          {
            x: x0 + w0,
            y: y0,
            width: Math.max(0, this.imgW - (x0 + w0)),
            height: h0
          }
        ];
      },
      handles() {
        const hc = HANDLE;
        const hEdge = HANDLE;
        const { rectX: x0, rectY: y0, rectWidth: rw, rectHeight: rh } = this;
        return {
          left: { x: x0 - hc / 2, y: y0 + rh / 2 - hEdge / 2, w: hc, h: hEdge },
          right: { x: x0 + rw - hc / 2, y: y0 + rh / 2 - hEdge / 2, w: hc, h: hEdge },
          top: { x: x0 + rw / 2 - hEdge / 2, y: y0 - hc / 2, w: hEdge, h: hc },
          bottom: { x: x0 + rw / 2 - hEdge / 2, y: y0 + rh - hc / 2, w: hEdge, h: hc }
        };
      },
      widthPercentLabel() {
        return this.$t(this.widthPercentI18nKey, {
          percent: this.displayWidthPercent
        });
      }
    },
    watch: {
      value: {
        immediate: true,
        handler(v) {
          const linear = v == null ? 1 : v;
          this.linearInner = linear;
          this.syncDisplayPercentFromLinear(linear);
        }
      }
    },
    beforeUnmount() {
      this.clearCommitTimer();
      this.teardownDrag();
    },
    methods: {
      widthPercentFromLinear(r) {
        return Math.round(r * 1000) / 10;
      },
      syncDisplayPercentFromLinear(r) {
        this.displayWidthPercent = this.widthPercentFromLinear(r);
      },
      clearCommitTimer() {
        if (this.commitTimerId != null) {
          clearTimeout(this.commitTimerId);
          this.commitTimerId = null;
        }
      },
      clientToSvg(evt) {
        const svg = this.$refs.svg;
        if (!svg || typeof svg.createSVGPoint !== 'function') return null;
        const pt = svg.createSVGPoint();
        pt.x = evt.clientX;
        pt.y = evt.clientY;
        const ctm = svg.getScreenCTM();
        if (!ctm) return null;
        return pt.matrixTransform(ctm.inverse());
      },
      clampLinear(r) {
        return Math.min(1, Math.max(MIN_LINEAR_R, r));
      },
      linearToEmitValue(c) {
        return c >= FULL_LINEAR_THRESHOLD ? null : c;
      },
      /** 박스는 즉시, 퍼센트·store는 COMMIT_INTERVAL_MS 간격 */
      applyLinearDuringDrag(r) {
        const c = this.clampLinear(r);
        this.linearInner = c;
        this.pendingLinear = c;
        if (this.commitTimerId == null) {
          this.commitTimerId = setTimeout(() => this.flushPendingCommit(), COMMIT_INTERVAL_MS);
        }
      },
      flushPendingCommit() {
        this.commitTimerId = null;
        if (this.pendingLinear == null) return;
        const c = this.pendingLinear;
        this.syncDisplayPercentFromLinear(c);
        this.$emit('input', this.linearToEmitValue(c));
        this.pendingLinear = null;
      },
      onEdgeMouseDown(edge, evt) {
        if (this.disabled) return;
        this.dragEdge = edge;
        const p = this.clientToSvg(evt);
        if (p) {
          this.applyEdgeToLinear(edge, p, true);
        }
        window.addEventListener('mousemove', this.onEdgeMouseMove);
        window.addEventListener('mouseup', this.onEdgeMouseUp);
      },
      onEdgeMouseMove(evt) {
        if (!this.dragEdge) return;
        const p = this.clientToSvg(evt);
        if (!p) return;
        this.applyEdgeToLinear(this.dragEdge, p, true);
      },
      applyEdgeToLinear(edge, p, throttleCommit) {
        let r;
        if (edge === 'right') {
          r = ((p.x - this.cx) * 2) / this.imgW;
        } else if (edge === 'left') {
          r = ((this.cx - p.x) * 2) / this.imgW;
        } else if (edge === 'bottom') {
          r = ((p.y - this.cy) * 2) / this.imgH;
        } else {
          r = ((this.cy - p.y) * 2) / this.imgH;
        }
        if (throttleCommit) {
          this.applyLinearDuringDrag(r);
        } else {
          const c = this.clampLinear(r);
          this.linearInner = c;
          this.syncDisplayPercentFromLinear(c);
          this.$emit('input', this.linearToEmitValue(c));
        }
      },
      onEdgeMouseUp() {
        this.dragEdge = null;
        this.clearCommitTimer();
        if (this.pendingLinear != null) {
          const c = this.pendingLinear;
          this.syncDisplayPercentFromLinear(c);
          this.$emit('input', this.linearToEmitValue(c));
          this.pendingLinear = null;
        }
        this.teardownDrag();
      },
      teardownDrag() {
        window.removeEventListener('mousemove', this.onEdgeMouseMove);
        window.removeEventListener('mouseup', this.onEdgeMouseUp);
      }
    }
  };
</script>

<style scoped>
  .ir-ai-detect-picker__svg {
    display: block;
    width: 100%;
    max-height: 300px;
    background: rgb(0 0 0 / 88%);
  }

  .ir-ai-detect-picker__dim {
    fill: rgb(0 0 0 / 40%);
    pointer-events: none;
  }

  .ir-ai-detect-picker__bbox {
    stroke: rgba(255, 255, 255, 0.92);
    stroke-width: 2;
    stroke-opacity: 0.95;
    filter: drop-shadow(0 0 1px #39ff14);
    pointer-events: none;
  }

  .ir-ai-detect-picker__hit {
    fill: rgb(57 255 20 / 20%);
    stroke: #adff2f;
    stroke-width: 2;
    vector-effect: non-scaling-stroke;
    cursor: grab;
  }

  .ir-ai-detect-picker__hit:active {
    cursor: grabbing;
  }

  .ir-ai-detect-picker__empty {
    min-height: 200px;
    border: thin dashed rgb(127 127 127 / 30%);
  }

  .ir-ai-detect-picker--disabled .ir-ai-detect-picker__svg {
    opacity: 0.55;
    pointer-events: none;
  }

  .ir-ai-detect-picker--fixed-frame {
    width: fit-content;
    max-width: 100%;
  }

  .ir-ai-detect-picker--guide {
    pointer-events: none;
  }
</style>
